import { faCircleNotch } from '@fortawesome/free-solid-svg-icons/faCircleNotch';
import classNames from 'classnames';
import omit from 'lodash/omit';
import React from 'react';
import { FormGroup } from 'reactstrap';

import { LocalizedMessage } from 'core/l10n/components';
import * as OvationsApi from 'core/ovations-api';
import FAIcon from 'core/ovations-components/FAIcon';
import { questionFieldIsDisabled, questionFieldIsRequired } from 'core/ovations-components/QuestionField';
import { monetize } from 'core/util/strings';

import QuestionFieldProps from './definitions/QuestionFieldProps';

export type CascadingDropdownQuestionProps = QuestionFieldProps<
    OvationsApi.Types.PromotionCascadingDropdownQuestion,
    OvationsApi.Types.PromotionCascadingDropdownQuestionAnswer,
    React.SelectHTMLAttributes<HTMLSelectElement>
>;

export interface CascadingDropdownQuestionState {
    options: { [dataSetPropertyId: number]: string[] };
    loading: { [dataSetPropertyId: number]: boolean };
}

const emptyAnswer: OvationsApi.Types.PromotionCascadingDropdownQuestionAnswer = {
    type: OvationsApi.Enums.QuestionType.CascadingDropdown,
    values: {},
};

class CascadingDropdownQuestionField extends React.Component<
    CascadingDropdownQuestionProps,
    CascadingDropdownQuestionState
> {
    constructor(props: CascadingDropdownQuestionProps) {
        super(props);
        this.state = {
            options: {},
            loading: {},
        };
    }

    componentDidMount() {
        const answer = this.props.answer || emptyAnswer;
        this.props.promotionQuestion.question.layers.forEach((layer) => {
            this.fetchOptionsForLayer(layer);
            const layerSelection = answer.values[layer.dataSetPropertyId];
            return !layerSelection;
        });
    }

    componentDidUpdate(prevProps: CascadingDropdownQuestionProps) {
        const answer = this.props.answer || emptyAnswer;
        const prevAnswer = prevProps.answer || emptyAnswer;
        if (prevProps.answer !== this.props.answer) {
            this.props.promotionQuestion.question.layers.some((layer, i, layers) => {
                const layerSelection = answer.values[layer.dataSetPropertyId];
                if (layerSelection && layerSelection !== prevAnswer.values[layer.dataSetPropertyId]) {
                    const nextLayer = layers[i + 1];
                    if (nextLayer) {
                        this.fetchOptionsForLayer(nextLayer);
                    }
                }
                return !layerSelection;
            });
        }
    }

    getFiltersForLayer(layer: OvationsApi.Types.CascadingDropdownLayer) {
        const filters: { [dataSetPropertyId: number]: string } = {};
        const { layers } = this.props.promotionQuestion.question;
        const answer = this.props.answer || emptyAnswer;
        layers.slice(0, layers.indexOf(layer)).forEach((l) => {
            filters[l.dataSetPropertyId] = answer.values[l.dataSetPropertyId];
        });

        return filters;
    }

    onDropdownChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
        if (!this.props.onChange) {
            return;
        }
        const { name, value } = e.currentTarget;
        const dataSetPropertyId = Number(name);
        const answer = this.props.answer || emptyAnswer;
        const allPropertyIds = this.props.promotionQuestion.question.layers.map((l) => l.dataSetPropertyId);
        const keysToRemove = allPropertyIds.slice(allPropertyIds.indexOf(dataSetPropertyId) + 1);
        this.props.onChange({
            ...answer,
            values: { ...omit(answer.values, keysToRemove), [dataSetPropertyId]: value },
        });
    };

    async fetchOptionsForLayer(layer: OvationsApi.Types.CascadingDropdownLayer) {
        if (!this.props.fetchCascadingDropdownOptions) {
            return;
        }
        const { question } = this.props.promotionQuestion;
        this.setState((state) => ({
            // We use setState's function syntax here to make sure we have the most up-to-date `state`
            // because this method could be called multiple times in the same tick.
            loading: { ...state.loading, [layer.dataSetPropertyId]: true },
            options: { ...state.options, [layer.dataSetPropertyId]: [] },
        }));
        const layerOptions = await this.props.fetchCascadingDropdownOptions(
            question.dataSetId,
            layer.dataSetPropertyId,
            this.getFiltersForLayer(layer),
        );

        const answer = this.props.answer || emptyAnswer;

        if (!answer.values[layer.dataSetPropertyId] && layerOptions.length === 1 && this.props.onChange) {
            const allPropertyIds = this.props.promotionQuestion.question.layers.map((l) => l.dataSetPropertyId);
            const keysToRemove = allPropertyIds.slice(allPropertyIds.indexOf(layer.dataSetPropertyId) + 1);
            this.props.onChange({
                ...answer,
                values: { ...omit(answer.values, keysToRemove), [layer.dataSetPropertyId]: layerOptions[0] },
            });
        }

        this.setState({
            loading: { ...this.state.loading, [layer.dataSetPropertyId]: false },
            options: { ...this.state.options, [layer.dataSetPropertyId]: layerOptions },
        });
    }

    renderLayerSpinner(layer: OvationsApi.Types.CascadingDropdownLayer) {
        if (!this.state.loading[layer.dataSetPropertyId]) {
            return;
        }
        return (
            <div className="position-absolute top-end-0 h-100 d-flex align-items-center me-4 pe-1">
                <FAIcon icon={faCircleNotch} className="spin spin--continuous text-muted" />
            </div>
        );
    }

    render() {
        const { layers } = this.props.promotionQuestion.question;
        const answer = this.props.answer || emptyAnswer;
        const hasFullAnswer = layers.every((l) => !!answer.values[l.dataSetPropertyId]);
        const required = questionFieldIsRequired(this.props);
        const shouldValidate = !hasFullAnswer && required;

        let previousKey: number;
        return (
            <div className={classNames('cascading-dropdown-set', { 'is-invalid': shouldValidate })}>
                {layers.map((layer) => {
                    const hasPrereqs = !previousKey || Boolean(answer.values[previousKey]);
                    if (hasPrereqs) {
                        previousKey = layer.dataSetPropertyId;
                    }

                    return (
                        <FormGroup key={layer.dataSetPropertyId} className="position-relative">
                            <select
                                name={String(layer.dataSetPropertyId)}
                                required={required}
                                disabled={!hasPrereqs || questionFieldIsDisabled(this.props)}
                                className="form-control"
                                onChange={this.onDropdownChange}
                                value={answer.values[layer.dataSetPropertyId] || ''}
                            >
                                <option value="">{layer.label}</option>
                                {hasPrereqs &&
                                    !this.state.loading[layer.dataSetPropertyId] &&
                                    (this.state.options[layer.dataSetPropertyId] || []).map((value) => (
                                        // Bug #51829: explicitly assigning value attribute
                                        // to preserve any whitespace formatting from data set
                                        <option value={value} key={value}>
                                            {layer.displayAsCurrency ? monetize(parseFloat(value)) : value}
                                        </option>
                                    ))}
                            </select>
                            {this.renderLayerSpinner(layer)}
                        </FormGroup>
                    );
                })}
                <span className="invalid-feedback">
                    <LocalizedMessage id="questionForm_errorMessage_requiredCascadingDropdown" />
                </span>
            </div>
        );
    }
}

export default CascadingDropdownQuestionField;
